import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Icon } from '../../../../components/common';
import {
  IconGoldenPinDuration,
  IconGoldenPinMap,
  IconGoldenPinSeenFirst,
  IconGoldenPinStandsOut,
} from '../../../../components/widgets/GoldenPinSvg';
import { getItem, setItem } from '../../../../utility/localStorageControl';
import { getGoldenPinOffer } from '../../utils/goldenPin';
import GoldenPinDialog from './GoldenPinDialog';
import {
  Body,
  CloseButton,
  Description,
  FeatureDivider,
  FeatureItem,
  FeatureStrip,
  Footer,
  HeroWrap,
  NewBadge,
  PrimaryButton,
  Title,
} from './styled';

export const getGoldenPinIntroKey = (userId) => `goldenPinIntroSeen_${userId}`;

const readSeen = (userId) => {
  try {
    return !!getItem(getGoldenPinIntroKey(userId));
  } catch (e) {
    return false;
  }
};

const writeSeen = (userId) => {
  try {
    setItem(getGoldenPinIntroKey(userId), true);
  } catch (e) {
    // Storage unavailable: the pop-up simply shows again next visit.
  }
};

/**
 * One-time "Introducing Golden Pin" announcement (legacy My Listings only). Closes only via
 * "Got it" or the close button; both persist the dismissal per user.
 */
const GoldenPinIntroModal = () => {
  const { t } = useTranslation();
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const { user } = useSelector((state) => state.app.loginUser);
  const [dismissed, setDismissed] = useState(false);
  const offer = getGoldenPinOffer(user);

  const open = !!user?.id && !dismissed && !readSeen(user.id);

  const dismiss = () => {
    writeSeen(user?.id);
    setDismissed(true);
  };

  if (!user?.id) return null;

  const features = [
    {
      Icon: IconGoldenPinStandsOut,
      title: t('Stands out'),
      text: t('Gold against every ordinary pin nearby'),
    },
    {
      Icon: IconGoldenPinSeenFirst,
      title: t('Seen first'),
      text: t('Buyers browsing the map notice it sooner'),
    },
    {
      Icon: IconGoldenPinDuration,
      // Credits and days come from the API; without the offer fall back to copy without numbers.
      title: offer ? t('goldenPinDays', { days: offer.days }) : t('For a limited time'),
      text: t('Then your pin goes back to normal'),
    },
  ];

  return (
    <GoldenPinDialog open={open} onClose={dismiss} width={500}>
      <HeroWrap $mobile={isMobile}>
        <IconGoldenPinMap
          variant={isMobile ? 'mweb' : 'web'}
          shimmer
          title={t('A gold pin standing out among ordinary price pins on the Bayut map')}
        />
        {!isMobile && (
          <CloseButton type="button" $mobile={isMobile} onClick={dismiss} aria-label={t('Got it')}>
            <Icon icon="IoMdClose" size="14px" />
          </CloseButton>
        )}
      </HeroWrap>

      <Body $mobile={isMobile}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Title $mobile={isMobile}>
            {t('Introducing Golden Pin')}
            <NewBadge $mobile={isMobile}>{t('New')}</NewBadge>
          </Title>
          <Description $mobile={isMobile}>
            {offer ? (
              <Trans
                i18nKey="goldenPinIntroDescription"
                values={{ credits: offer.credits, days: offer.days }}
                components={{ b: <strong /> }}
              />
            ) : (
              t("Spend credits to turn one listing's pin gold on the Bayut map.")
            )}
          </Description>
        </div>
        <FeatureStrip $mobile={isMobile}>
          {features.map(({ Icon, title, text }, index) => (
            <React.Fragment key={title}>
              {index > 0 && !isMobile && <FeatureDivider />}
              <FeatureItem $mobile={isMobile}>
                <span className="gp-feature-icon">
                  <Icon />
                </span>
                <div style={isMobile ? { display: 'flex', flexDirection: 'column', gap: 2 } : { display: 'contents' }}>
                  <span className="gp-feature-title">{title}</span>
                  <span className="gp-feature-text">{text}</span>
                </div>
              </FeatureItem>
            </React.Fragment>
          ))}
        </FeatureStrip>
      </Body>

      <Footer $mobile={isMobile} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
        <PrimaryButton type="button" $mobile={isMobile} $large $block onClick={dismiss}>
          {t('Got it')}
        </PrimaryButton>
      </Footer>
    </GoldenPinDialog>
  );
};

export default GoldenPinIntroModal;
